// Import general mixin for shared functionality.
import mixin from './mixin.js';

// Import general functions for shared functionality.
import { setCookie, getCookie, deleteCookie } from '../functions.js';
import constants from '../constants.js';

// Import axios for API consuming in a clean interface.
import axios from 'axios';

// Import libraries
import { Stuck } from 'stuck-js';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

const defaultApp = {
    mixins: [mixin],
    data() {
        return {
            dataApi: '',
            bannerApi: '',
            dataLimit: 10, // Default limit in BE
            dataSortKey: '',
            dataSortDirection: '',
            dataAmountSentence: '',
            dataWidget: false,
            dataProduction: false,
            dataOverruleIds: [],
            dataOverruleCmsIds: [],
            dataGenreOverrule: [],
            dataAgendaItemGenres: [],
            dataTagsOverrule: [],
            dataExcludeProductions: [],
            dataUmbracoOrder: false,
            dataTarget: '',
            dataTags: [],
            culture: 'nl-NL',
            language: 'nl',
            loading: false,
            loadMore: false,
            facetState: [],
            data: {
                items: [],
                facets: [],
                queryCount: 0,
            },
            banners: [],
            searchFrom: '',
            searchTo: '',
            page: 1,
            searchText: '',
            constants: constants
        }
    },
    // The computed object holds checks for facets set, filters chosen and filters active.
    // Furthermore, reactive properties for orderItems, filterItems and the queryModel are kept track of.
    computed: {
        facetsSet() {
            return this.data.facets.length;
        },
        itemsSet() {
            return this.data.items.length;
        },
        filtersChosen() {
            return (this.facetsSet &&
                    this.data.facets.some(facet => facet.values.length)) ||
                    this.dataOverruleIds.length ||
                    this.dataOverruleCmsIds.length ||
                    this.searchFrom.length ||
                    this.searchTo.length ||
                    this.dataGenreOverrule.length ||
                    this.dataTagsOverrule.length ||
                    this.dataExcludeProductions.length
        },
        filtersActive() {
            const hasFilterdFacetSelection = this.data.facets.some(facet => {
                if (!facet.selected) return false;

                const isLocationFacet = facet.alias === this.constants.keys.location;
                const selectedOptions = facet.options.filter(opt => opt.selected);

                if (isLocationFacet && selectedOptions.length === 1 && selectedOptions[0].valueAlias === this.dataTarget) {
                    return false;
                }

                return true;
            });
            return (
                this.facetsSet &&
                hasFilterdFacetSelection ||
                this.searchText.length ||
                this.searchFrom.length ||
                this.searchTo.length
            );
        },
        orderItems() {
            if (!this.dataSortKey && !this.dataSortDirection) return [];
            return [
                {
                    Key: this.dataSortKey,
                    Direction: this.dataSortDirection,
                }
            ]
        },
        filterItems() {
            if (!this.filtersChosen) {
                if (this.dataTarget) {
                    return [{
                        Key: this.constants.keys.location,
                        Values: [this.dataTarget],
                        Operator: this.constants.operators.in
                    }]
                } else {
                    return []
                }
            }
            if (this.dataOverruleIds.length) {
                return [
                    {
                        Key: this.constants.keys.id,
                        Values: this.dataOverruleIds,
                        Operator: this.constants.operators.in
                    }
                ]
            }
            let tempFilterArray = this.data.facets.map(facet => {
                if (facet.values.length) {
                    return {
                        Key: facet.alias,
                        Values: facet.values,
                        Operator: facet.alias === this.constants.keys.date ? this.constants.operators.equals : this.constants.operators.in
                    }
                } 
                return null
            }).filter(item => item !== null);
            
            return tempFilterArray;
        },
        queryModel() {
            return {
                Limit: this.dataLimit,
                Offset: this.data.items.length && !this.dataWidget ? (this.page - 1) * this.dataLimit : 0,
                OrderItems: this.orderItems,
                FilterItems: this.filterItems,
                Text: this.searchText
            }
        },
        amountSentence() {
            if (!this.data || !this.data.queryCount || !this.data.totalCount) return null;
            return this.dataAmountSentence.replace(this.constants.magicStrings.queryCount, this.data.queryCount);
        },
    },
    methods: {
        async init() {
            // Set loading to true.
            this.loading = true;

            // Set data props according to data attributes in HTML template.
            if (this.$el.dataset.widget) this.dataWidget = this.$el.dataset.widget ? true : false;
            if (this.$el.dataset.production) this.dataProduction = this.$el.dataset.production ? true : false;
            if (this.$el.dataset.overruleids) this.dataOverruleIds = this.$el.dataset.overruleids.split(',');
            if (this.$el.dataset.overrulecmsids) this.dataOverruleCmsIds = this.$el.dataset.overrulecmsids.split(',');
            if (this.$el.dataset.genreoverrule) this.dataGenreOverrule = this.$el.dataset.genreoverrule.split(',');
            if (this.$el.dataset.agendaitemgenres) this.dataAgendaItemGenres = this.$el.dataset.agendaitemgenres.split(',');
            this.dataGenreOverrule = [...new Set([...this.dataGenreOverrule, ...this.dataAgendaItemGenres])];
            if (this.$el.dataset.tagoverrule) this.dataTagsOverrule = this.$el.dataset.tagoverrule.split(',');
            if (this.$el.dataset.excludeproductions) this.dataExcludeProductions = this.$el.dataset.excludeproductions.split(',');
            if (this.$el.dataset.currentculture) this.culture = this.$el.dataset.currentculture;
            this.language = this.culture.split('-')[0];
            if (this.$el.dataset.api) this.dataApi = this.$el.dataset.api;
            if (this.$el.dataset.limit) this.dataLimit = parseInt(this.$el.dataset.limit);
            if (this.$el.dataset.sortkey) this.dataSortKey = this.$el.dataset.sortkey;
            if (this.$el.dataset.sortdirection) this.dataSortDirection = this.$el.dataset.sortdirection;
            if (this.$el.dataset.showxamountoftotalitems) this.dataAmountsSentence = this.$el.dataset.showxamountoftotalitems;
            if (this.$el.dataset.target) this.dataTarget = this.$el.dataset.target.toLowerCase();
            if (this.dataTodayLabel && this.$el.dataset.todaylabel) this.dataTodayLabel = this.$el.dataset.todaylabel;
            if (this.dataWeekendLabel && this.$el.dataset.weekendlabel) this.dataWeekendLabel = this.$el.dataset.weekendlabel;
            if (this.dataWeekLabel && this.$el.dataset.weeklabel) this.dataWeekLabel = this.$el.dataset.weeklabel;
            if (this.dataMonthLabel && this.$el.dataset.monthlabel) this.dataMonthLabel = this.$el.dataset.monthlabel;
            if (this.$el.dataset.tags) this.dataTags = this.$el.dataset.tags.split(',');
            if (this.$el.dataset.bannerapi) this.bannerApi = this.$el.dataset.bannerapi;
            if (this.$el.dataset.amountSentence) this.dataAmountSentence = this.$el.dataset.amountSentence;
            if (this.$el.dataset.umbracoOrder) this.dataUmbracoOrder = this.$el.dataset.umbracoOrder === 'True';
            // Construct query model and alter data props based on URL search params.

            if (this.bannerApi.length) {
                const today = new Date()
                const bannerResponse = await this.fetchBanners();
                if (bannerResponse && bannerResponse.status == 200) this.banners = bannerResponse.data.filter(b => new Date(b.start) >= today || (new Date(b.start) < today && b.end && new Date(b.end) >= today)).map(b => ({ ...b, type: 'banner' }));
            }

            let tempLimit = this.dataLimit;
            this.resolveParams();

            // Prefetch data for overrule ids.
            if (this.dataOverruleIds.length) {
                const overruleResponse = await this.fetchData();
                // Make sure ordering corresponds to picking order in CMS
                if (overruleResponse && overruleResponse.data && overruleResponse.data.length > 1 && this.dataUmbracoOrder) {
                    overruleResponse.data = overruleResponse.data.sort((a, b) => this.dataOverruleIds.indexOf(a.cmsId) - this.dataOverruleIds.indexOf(b.cmsId));
                }
                this.setData(overruleResponse);
                this.dataOverruleIds = [];
                if (this.data.items.length < this.dataLimit) {
                    this.dataLimit = this.dataLimit - this.data.items.length;
                } else {
                    this.dataLimit = 0;
                }
            }

            if (this.dataOverruleCmsIds.length) {
                const overruleResponse = await this.fetchData();
                // Make sure ordering corresponds to picking order in CMS
                if (overruleResponse && overruleResponse.data && overruleResponse.data.length > 1 && this.dataUmbracoOrder) {
                    overruleResponse.data = overruleResponse.data.sort((a, b) => this.dataOverruleCmsIds.indexOf(a.cmsId) - this.dataOverruleCmsIds.indexOf(b.cmsId));
                }
                this.setData(overruleResponse);
                this.dataOverruleCmsIds = [];
                if (this.data.items.length < this.dataLimit && (this.dataGenreOverrule.length || this.dataTagsOverrule.length)) {
                    this.dataLimit = this.dataLimit - this.data.items.length;
                } else {
                    this.dataLimit = 0;
                }
            }

            if (this.dataLimit) {
                let tempTarget = this.dataTarget;
                if (this.dataGenreOverrule.length || this.dataTagsOverrule.length) {
                    this.dataTarget = '';
                }
                // Initial data fetch.
                const response = await this.fetchData();
                let replace = !this.itemsSet;
                this.setData(response, replace);
                this.dataTarget = tempTarget;

                // Remove duplicates from items.
                if (this.itemsSet) {
                    this.data.items = this.removeDuplicatesById(this.data.items);
                    if (this.dataWidget) {
                        this.data.items = this.data.items.slice(0, tempLimit);

                        if (!this.dataUmbracoOrder) {
                            this.data.items = this.data.items.sort((a, b) => new Date(a.start) - new Date(b.start));
                        }
                    }
                } else {
                    if (this.dataWidget) {
                        if (this.dataGenreOverrule.length && !this.data.items.length) {
                            this.dataGenreOverrule = [];
                            const response = await this.fetchData();
                            this.setData(response, true);
                        }
                    }
                }
            }

            this.dataLimit = tempLimit;

            // Set loading to false.
            this.loading = false;
            this.$nextTick(() => {
                new Stuck([
                    {
                        selector: '.page-agenda-overview__bar',
                        wrapper: 'body'
                    },
                ]);
                if (this.$refs.slider) {
                    new Swiper(this.$refs.slider, {
                        slidesPerView: 1.2,
                        breakpoints: {
                            600: {
                                slidesPerView: 'auto'
                            },
                            768: {
                                slidesPerView: 2.4
                            },
                            1024: {
                                slidesPerView: 3
                            }
                        },
                        spaceBetween: 32,
                        navigation: {
                            nextEl: this.$refs.sliderNext,
                            prevEl: this.$refs.sliderPrev,
                        },
                        pagination: {
                            el: this.$refs.sliderPagination,
                            clickable: true
                        }
                    });
                }
            });
        },
        // The fetchData method fetches and handles API data, mainly facets and resulting items. 
        async fetchData() {
            try {
                const response = await axios.post(this.dataApi, this.queryModel);
                return response;
            } catch (error) {
                console.log(error);
            }
        },
        // The fetchInitialData method resets the data and page props, loads data anew and sets the data.
        async fetchInitialData() {
            this.page = 1;
            const response = await this.fetchData();
            this.setData(response, true);
            this.updateUrl();
        },
        async fetchBanners() {
            let data = {
                FilterItems: [{
                    Key: this.constants.keys.banner,
                    Values: [this.dataTarget],
                    Operator: this.constants.operators.in
                }]
            };
            try {
                const response = await axios.post(this.bannerApi, data);
                return response;
            } catch (error) {
                console.log(error);
            }
        },
        // Lazy loading data chunks handler.
        async loadmore() {
            this.loadMore = true;
            this.page++;
            const response = await this.fetchData();
            this.setData(response);
            this.updateUrl();
            this.loadMore = false;
        },
        // Method to set data items array or append items to it.
        setData(response, replace = false) {
            if (!response || response.status != 200 || !response.data) return;
            this.data.queryCount = response.data.queryCount;
            if (!this.itemsSet || replace) {
                this.data.items = this.dataWidget ? response.data : response.data.items;
            } else {
                if (this.dataWidget) {
                    response.data.forEach(item => {
                        this.data.items.push(item);
                    });
                } else {
                    response.data.items.forEach(item => {
                        this.data.items.push(item);
                    });
                }
            }
            this.setFacets(response);
        },
        resolveParams() {
            const urlParams = new URLSearchParams(window.location.search);

            let pageParam = urlParams.has(this.constants.queryParams.page) ? parseInt(urlParams.get(this.constants.queryParams.page)) : 0;
            if (pageParam) urlParams.delete(this.constants.queryParams.page);
            if (pageParam < 2) pageParam = 0;
            this.searchText = urlParams.has(this.constants.queryParams.text) ? urlParams.get(this.constants.queryParams.text) : '';
            if (this.searchText) urlParams.delete(this.constants.queryParams.text);
            let filterParams = urlParams.toString() ? urlParams : null;
            // Preset values for facets based on query params.
            if (filterParams) {
                let filterQuery = [];
                filterParams.forEach((value, key) => {
                    filterQuery.push({ Key: key, Values: value.split(','), Operator: this.constants.operators.in });
                });
                this.queryModel.FilterItems = filterQuery;
            }
            if (pageParam && !this.dataWidget) {
                this.page = pageParam;
                this.dataLimit = this.dataLimit * this.page;
            }
        },
        // Method to set facet values based on URL search params.
        setFacetValuesFromUrl(filterParams) {
            if (!this.facetsSet) return;
            this.data.facets.forEach(facet => {
                if (filterParams.has(facet.alias)) {
                    let valueArray = filterParams.get(facet.alias).split(',');
                    facet.values = valueArray
                        .map(alias => facet.options.find(option => option.valueAlias === alias))
                        .map(option => option.valueAlias);
                }
            });
        },
        // Method to set facets array.
        setFacets(response) {
            if (!response || response.status != 200 || !response.data.facets) return;
            this.data.facets = response.data.facets
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(facet => ({
                    ...facet,
                    values: facet.options.filter(option => option.selected).map(option => option.valueAlias),
                }));
            if (!this.facetState.length) {
                this.data.facets.forEach(() => {
                    this.facetState.push({
                        facetOpen: false
                    });
                });
            }
            this.data.queryCount = response.data.queryCount;
            this.data.totalCount = response.data.totalCount;
        },
        // Method for removing duplicates from array by id.
        removeDuplicatesById(array) {
            return array.reduce((accumulator, current) => {
                if (!accumulator.find((item) => item.id === current.id)) {
                    accumulator.push(current);
                }
                return accumulator;
            }, []);
        },
        toggleOption(facetAlias, optionAlias) {
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            let changedOption = changedFacet.options.find(option => option.valueAlias === optionAlias);
            if (changedFacet && changedFacet.values.includes(optionAlias)) {
                changedFacet.values = changedFacet.values.filter(value => value !== optionAlias);
                changedOption.selected = false;
            } else {
                changedFacet.values.push(optionAlias);
                changedOption.selected = true;
            }
            changedFacet.selected = changedFacet.values.length;
            this.fetchInitialData();
        },
        selectOption(facetAlias, optionAlias) {
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            let changedOption = changedFacet.options.find(option => option.valueAlias === optionAlias);
            changedFacet.values = [];
            changedFacet.options.forEach(option => {
                option.selected = false;
            });
            changedFacet.values.push(optionAlias);
            changedOption.selected = true;
            changedFacet.selected = changedFacet.values.length;
            this.fetchInitialData();
        },
        resetMultiDropdown(facetAlias) {
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            changedFacet.values = [];
             changedFacet.options.forEach(option => {
                option.selected = false;
            });
            changedFacet.selected = false;
            this.fetchInitialData();
        },
        // Data alteration function for resetting all active filters and reloading data.
        reset() {
            this.data.facets.forEach(facet => facet.values = []);
            this.searchText = '';
            this.loading = true;
            this.fetchInitialData();
            this.loading = false;
        },
        // Function for updating browser URL and state history with changed filters, search input or page.
        updateUrl() {
            if (!this.queryModel.Text && !this.queryModel.FilterItems.length && this.page === 1) {
                history.pushState({}, null, window.location.pathname);
                return;
            }
            const urlParams = new URLSearchParams();
            if (this.queryModel.Text) urlParams.append(this.constants.queryParams.text, this.queryModel.Text);
            if (this.queryModel.FilterItems.length) {
                this.queryModel.FilterItems.forEach(filterItem => {
                    if (filterItem.Values[0] !== this.dataTarget && filterItem.Operator !== this.constants.operators.notIn) {
                        urlParams.append(filterItem.Key, filterItem.Values.join())
                    }
                });
            }
            if (this.page > 1) urlParams.append(this.constants.queryParams.page, this.page);
            if (urlParams.size) {
                history.pushState({}, null, window.location.pathname + '?' + urlParams.toString());
            } else {
                history.pushState({}, null, window.location.pathname);
            }
        },
        // Check if a new month should be shown in the template and return the display value if so.
        showMonth(index, date) {
            let returnVal = null;
            const dateObj = this.toDateObj(date);
            if (index === 0) {
                returnVal = new Intl.DateTimeFormat(this.culture, { month: 'long' }).format(dateObj) + ' ' + dateObj.getFullYear();
            } else {
                const prevEvent = this.data.items[index - 1];
                if (prevEvent) {
                    const prevDateObj = this.toDateObj(prevEvent.start);
                    if (dateObj.getMonth() !== prevDateObj.getMonth() || dateObj.getYear() !== prevDateObj.getYear()) {
                        returnVal = new Intl.DateTimeFormat(this.culture, { month: 'long' }).format(dateObj) + ' ' + dateObj.getFullYear();
                    }
                }
            }
            return returnVal;
        }
    },
    created() {
        // Check which view is set as cookie.
        if (getCookie(this.constants.cookies.listBlockView) !== "") this.blockView = false;
        // Data alteration function for reloading data with textual search input.
        this.changeSearchText = this.debounce(() => {
            this.fetchInitialData();
        });
    },
    // Call init method in mounted hook.
    mounted() {
        this.init();
    }
};

export default defaultApp;
